import type { FormField } from '@lucid/lego';
import type {
  Prospect,
  TestDriveSchedule,
} from '../../../../../api/services/lupp/prospect/dto.prospect';
import { formatDateForPicker, getNextEngagementDate, getLatestNoteContent } from '../utils';
import { formatDateTime } from '../../../../../utils/tools';
import {
  carMakerOptions,
  financeTypeOptions,
  interestedModelOptions,
  preferredContactMethodOptions,
  purchaseTimingOptions,
  purchaseTypeOptions,
  statusOptions,
  testDriveTimeOptions,
  titleOptions,
} from '../../../constants';
/**
 * @param prospectOwnerName
 * @param prospectOwnerLocation
 */
export const createLeadFormFields = (
  prospect?: Prospect,
  onCancel?: () => void,
  organizationName?: string,
  prospectOwnerName?: string,
  prospectOwnerLocation?: string,
  onPostalCodeChange?: (value: unknown) => void
): FormField[] => [
  {
    id: 'personalInformation',
    fieldType: 'group',
    width: 'full',
    title: '',
    children: [
      {
        fieldType: 'content',
        width: 'quarter',
        id: 'prospectOwner',
        label: 'Prospect Owner',
        content: prospectOwnerName || '-',
      },
      {
        fieldType: 'content',
        width: 'quarter',
        id: 'prospectOwnerLocation',
        label: 'Prospect Owner Location',
        content: prospectOwnerLocation || '-',
      },
      {
        fieldType: 'content',
        width: 'quarter',
        id: 'prospectCaptured',
        label: 'Prospect Captured',
        content: formatDateTime(prospect?.createdDatetime),
      },
      {
        fieldType: 'content',
        width: 'quarter',
        id: 'lastModified',
        label: 'Last Modified',
        content: formatDateTime(prospect?.updatedDatetime),
      },
    ],
  },
  // Personal Information Section
  {
    id: 'informationGroup',
    fieldType: 'group',
    title: '',
    width: 'full',
    children: [
      {
        id: 'leadFirstName',
        fieldType: 'text',
        name: 'firstName',
        apiKey: 'leadFirstName',
        label: 'First Name',
        placeholder: 'Enter First Name',
        defaultValue: prospect?.prospectFirstName || '',
        required: true,
        width: 'quarter',
        validationMessages: {
          required: 'First name is required',
        },
      },
      {
        id: 'prospectMiddleName',
        fieldType: 'text',
        name: 'prospectMiddleName',
        apiKey: 'prospectMiddleName',
        label: 'Middle Name',
        width: 'quarter',
        placeholder: 'Enter Middle Name',
        defaultValue: prospect?.prospectMiddleName || '',
      },
      {
        id: 'leadLastName',
        fieldType: 'text',
        name: 'lastName',
        apiKey: 'leadLastName',
        label: 'Last Name',
        placeholder: 'Enter Last Name',
        defaultValue: prospect?.prospectLastName || '',
        required: true,
        width: 'quarter',
        validationMessages: {
          required: 'Last name is required',
        },
      },
      {
        id: 'prospectTitle',
        fieldType: 'dropdown',
        name: 'prospectTitle',
        apiKey: 'prospectTitle',
        label: 'Title',
        placeholder: 'Select',
        defaultValue: prospect?.prospectTitle || '',
        required: true,
        width: 'quarter',
        options: titleOptions,
        validationMessages: {
          required: 'Title is required',
        },
      },
      {
        id: 'leadEmail',
        fieldType: 'email',
        name: 'email',
        apiKey: 'leadEmail',
        label: 'Email',
        defaultValue: prospect?.prospectEmail || '',
        required: true,
        disabled: true,
        width: 'half',
        validationMessages: {
          required: 'Email is required',
          pattern: 'Please enter a valid email address',
        },
      },
      {
        id: 'leadPhone',
        fieldType: 'phone',
        name: 'phone',
        apiKey: 'leadPhone',
        label: 'Phone',
        value: prospect?.prospectPhone || '',
        required: false,
        width: 'half',
        selectedOption: 'US',
        validationMessages: {
          required: 'Phone is required',
          pattern: 'Please enter a valid phone number',
        },
      },
      {
        id: 'zipCode',
        fieldType: 'text',
        name: 'zipCode',
        apiKey: 'zipCode',
        label: 'Postal Code',
        placeholder: 'Enter Postal Code',
        defaultValue: prospect?.zipCode || '',
        required: true,
        width: 'full',
        onChange: onPostalCodeChange,
        validationMessages: {
          required: 'Postal code is required',
        },
      },
      {
        id: 'city',
        fieldType: 'text',
        name: 'city',
        apiKey: 'city',
        label: 'City/Region',
        placeholder: 'Enter City/Region',
        defaultValue: prospect?.city || prospect?.region || '',
        required: true,
        width: 'half',
        validationMessages: {
          required: 'City/Region is required',
        },
      },
      {
        id: 'country',
        fieldType: 'text',
        name: 'country',
        apiKey: 'country',
        label: 'Country',
        placeholder: '',
        defaultValue: prospect?.countryCode || '',
        disabled: true,
        width: 'half',
      },
      {
        id: 'sourceOfProspect',
        fieldType: 'content',
        label: 'Source of Prospect',
        width: 'full',
        content:
          prospect?.prospectSourceName ||
          prospect?.sourceName ||
          prospect?.prospectCaptureForm ||
          '-',
      },
    ],
  },
  // Purchase Profile Section
  {
    id: 'interestedModel',
    fieldType: 'dropdown',
    name: 'interestedModel',
    apiKey: 'interestedModel',
    label: 'Interested Model/Trim',
    placeholder: 'Select a Model',
    defaultValue: prospect?.interestedModel || '',
    options: interestedModelOptions,
  },
  {
    id: 'interestedVehicleCondition',
    fieldType: 'radio_group',
    name: 'interestedVehicleCondition',
    apiKey: 'interestedVehicleCondition',
    label: 'Interested Vehicle Condition',
    width: 'full',
    layout: 'horizontal',
    options: [
      {
        label: 'New Vehicle',
        value: 'new',
        default:
          !prospect?.interestedVehicleCondition || prospect?.interestedVehicleCondition === 'new',
      },
      {
        label: 'Used Vehicle',
        value: 'used',
        default: prospect?.interestedVehicleCondition === 'used',
      },
    ],
  },
  {
    id: 'currentCarMake',
    fieldType: 'dropdown',
    name: 'currentCarMake',
    apiKey: 'currentCarMake',
    label: 'Make of Current Car',
    placeholder: 'Select Make',
    defaultValue: prospect?.currentCarMake || '',
    width: 'full',
    options: carMakerOptions,
  },
  {
    id: 'purchaseType',
    fieldType: 'dropdown',
    name: 'purchaseType',
    apiKey: 'purchaseType',
    label: 'Purchase Type',
    placeholder: 'Select Purchase Type',
    defaultValue: prospect?.purchaseType || '',
    width: 'full',
    options: purchaseTypeOptions,
  },
  {
    id: 'financeType',
    fieldType: 'dropdown',
    name: 'financeType',
    apiKey: 'financeType',
    label: 'Finance',
    placeholder: 'Select Finance Type',
    defaultValue: prospect?.financeType || '',
    width: 'full',
    options: financeTypeOptions,
  },
  {
    id: 'purchaseTiming',
    fieldType: 'dropdown',
    name: 'purchaseTiming',
    apiKey: 'purchaseTiming',
    label: 'Timing',
    placeholder: 'Select time',
    defaultValue: prospect?.purchaseTiming || '',
    width: 'full',
    options: purchaseTimingOptions,
  },
  {
    id: 'nextEngagementDate',
    fieldType: 'date_picker',
    name: 'nextEngagementDate',
    apiKey: 'nextEngagementDate',
    label: 'Next Engagement Date',
    required: true,
    width: 'full',
    size: 'medium',
    minDate: 'today',
    defaultValue: getNextEngagementDate(prospect),
    validationMessages: {
      required: 'Next Engagement Date is required',
    },
  },
  {
    id: 'preferredContactMethod',
    fieldType: 'dropdown',
    name: 'preferredContactMethod',
    apiKey: 'preferredContactMethod',
    label: 'Preferred Contact Method',
    placeholder: 'Select Preferred Contact Method',
    defaultValue: prospect?.preferredContactMethod || '',
    width: 'full',
    options: preferredContactMethodOptions,
  },
  // Consent Section
  {
    id: 'emailConsent',
    fieldType: 'radio_group',
    name: 'emailConsent',
    apiKey: 'isEmailOptIn',
    label: 'Email Consent',
    width: 'full',
    layout: 'horizontal',
    options: [
      { label: 'Email Opt In', value: 'emailOptIn', default: prospect?.isEmailOptIn ?? true },
      { label: 'Email Opt Out', value: 'emailOptOut', default: prospect?.isEmailOptIn === false },
    ],
  },
  {
    id: 'callConsent',
    fieldType: 'radio_group',
    name: 'callConsent',
    apiKey: 'isCallOptIn',
    label: 'Call Consent',
    width: 'full',
    layout: 'horizontal',
    options: [
      { label: 'Call Opt In', value: 'callOptIn', default: prospect?.isCallOptIn === true },
      {
        label: 'Call Opt Out',
        value: 'callOptOut',
        default: prospect?.isCallOptIn === false || prospect?.isCallOptIn === undefined,
      },
    ],
  },
  {
    id: 'notes',
    fieldType: 'textarea',
    name: 'notes',
    apiKey: 'noteText',
    label: 'Notes',
    placeholder: 'Enter Notes',
    defaultValue: getLatestNoteContent(prospect?.notes) || prospect?.noteText || '',
    width: 'full',
  },
  {
    apiKey: 'terms',
    fieldType: 'disclaimers',
    id: 'terms',
    name: 'terms',
    required: false,
    terms: `    
    By clicking Save, I confirm that I have informed the prospect that their personal data will be processed by ${organizationName || '-'}, Lucid Europe B.V., 
    and other relevant Lucid entities (such as local affiliates, importers, and dealers) 
    in accordance with <a href="https://lucidmotors.com/de-de/legal#privacy-policy" target="_blank" rel="noopener noreferrer">Lucid's Privacy Policy</a> and, where applicable, the Agent's own privacy policy.
    `,
  },
  {
    id: 'formButtons',
    fieldType: 'buttonGroup',
    align: 'spaceBetween',
    buttons: [
      {
        id: 'cancelButton',
        label: 'Cancel',
        size: 'medium',
        type: 'secondary',
        color: 'neutral',
        theme: 'dark',
        isFluid: true,
        onClick: onCancel,
      },
      {
        id: 'submitButton1',
        label: 'Save',
        type: 'primary',
        size: 'medium',
        color: 'neutral',
        theme: 'light',
        isFluid: true,
        disabled: prospect?.statusName?.toLowerCase() === 'closed',
      },
    ],
  },
];

/**
 * Format time range from start and end datetime for dropdown matching
 * @param startDatetime - ISO datetime string for start
 * @param endDatetime - ISO datetime string for end
 * @returns Time range string matching dropdown options (e.g., "11:30-12:00")
 */
const formatTimeSlot = (startDatetime?: string, endDatetime?: string): string => {
  if (!startDatetime || !endDatetime) return '';
  try {
    const start = new Date(startDatetime);
    const end = new Date(endDatetime);
    const startHour = start.getUTCHours();
    const startMinute = start.getUTCMinutes();
    const endHour = end.getUTCHours();
    const endMinute = end.getUTCMinutes();

    const formatTime = (hour: number, minute: number): string => {
      const h = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const m = minute.toString().padStart(2, '0');
      return `${h}:${m}`;
    };

    return `${formatTime(startHour, startMinute)}-${formatTime(endHour, endMinute)}`;
  } catch {
    return '';
  }
};

/**
 * Creates form fields for Test Drive tab
 * @param onCancel - Cancel button handler
 * @param testDriveSchedule - The test drive schedule data (optional)
 * @param location - User's location from userIdentity
 * @param organizationName - Organization name for disclaimer
 * @returns FormField array for test drive form
 */
export const createTestDriveFormFields = (
  prospect?: Prospect,
  onCancel?: () => void,
  testDriveSchedule?: TestDriveSchedule,
  location?: string,
  organizationName?: string
): FormField[] => [
  {
    id: 'testDriveHeader',
    fieldType: 'group',
    width: 'full',
    title: 'Schedule Test Drive',
    children: [
      {
        id: 'testDriveNow',
        fieldType: 'checkbox_group',
        name: 'testDriveNow',
        apiKey: 'testDriveNow',
        label: 'Test Drive Now',
        width: 'half',
        layout: 'horizontal',
        options: [{ label: 'Yes', value: 'yes' }],
        defaultValue: testDriveSchedule?.isActive ? ['yes'] : [],
      },
      {
        fieldType: 'content',
        width: 'half',
        id: 'location',
        label: 'Location',
        content: location || '-',
      },
      {
        id: 'scheduledDate',
        fieldType: 'date_picker',
        name: 'scheduledDate',
        apiKey: 'scheduledDate',
        label: 'Scheduled Date',
        placeholder: 'Select Date',
        required: true,
        width: 'half',
        size: 'medium',
        minDate: 'today',
        defaultValue: formatDateForPicker(testDriveSchedule?.scheduledStartDatetime),
        dependency: {
          name: 'testDriveNow',
          value: 'yes',
        },
        validationMessages: {
          required: 'Scheduled date is required',
        },
      },
      {
        id: 'scheduledTime',
        fieldType: 'dropdown',
        name: 'scheduledTime',
        apiKey: 'scheduledTime',
        label: 'Scheduled Time',
        placeholder: 'Select Time',
        required: true,
        width: 'half',
        defaultValue: formatTimeSlot(
          testDriveSchedule?.scheduledStartDatetime,
          testDriveSchedule?.scheduledEndDatetime
        ),
        options: testDriveTimeOptions,
        dependency: {
          name: 'testDriveNow',
          value: 'yes',
        },
        validationMessages: {
          required: 'Scheduled time is required',
        },
      },
      {
        id: 'status',
        fieldType: 'dropdown',
        name: 'status',
        apiKey: 'status',
        label: 'Status',
        placeholder: 'Select Status',
        required: true,
        width: 'full',
        defaultValue: testDriveSchedule?.testDriveStatus || 'scheduled',
        options: statusOptions,
        dependency: {
          name: 'testDriveNow',
          value: 'yes',
        },
        validationMessages: {
          required: 'Status is required',
        },
      },
      {
        id: 'selectedModelTrim',
        fieldType: 'dropdown',
        name: 'selectedModelTrim',
        apiKey: 'selectedModelTrim',
        label: 'Selected Model/Trim',
        placeholder: 'Select a Model',
        width: 'full',
        defaultValue: testDriveSchedule?.trim || testDriveSchedule?.model || '',
        options: interestedModelOptions,
        dependency: {
          name: 'testDriveNow',
          value: 'yes',
        },
      },
    ],
  },
  {
    apiKey: 'terms',
    fieldType: 'disclaimers',
    id: 'terms',
    name: 'terms',
    required: false,
    terms: `    
    By clicking Save, I confirm that I have informed the prospect that their personal data will be processed by ${organizationName || '-'}, Lucid Europe B.V., 
    and other relevant Lucid entities (such as local affiliates, importers, and dealers) 
    in accordance with <a href="https://lucidmotors.com/de-de/legal#privacy-policy" target="_blank" rel="noopener noreferrer">Lucid's Privacy Policy</a> and, where applicable, the Agent's own privacy policy.
    `,
  },
  {
    id: 'formButtons',
    fieldType: 'buttonGroup',
    align: 'spaceBetween',
    buttons: [
      {
        id: 'cancelButton',
        label: 'Cancel',
        size: 'medium',
        type: 'secondary',
        color: 'neutral',
        theme: 'dark',
        isFluid: true,
        onClick: onCancel,
      },
      {
        id: 'submitButton',
        label: 'Save',
        type: 'primary',
        size: 'medium',
        color: 'neutral',
        theme: 'light',
        isFluid: true,
        disabled: prospect?.statusName?.toLowerCase() === 'closed',
      },
    ],
  },
];
